/**
 * TemporalEchoSkillEffect — rewind mechanic for the Astral oracle path.
 *
 * First activation stamps position and HP. For 6 seconds (base), everything
 * is provisional. Second activation or lethal damage triggers a clean revert
 * to the stamp — position and HP restored, provisional damage undone.
 *
 * Upgrade hooks:
 * - temporal_echo_upgrade_1 (Extended Window):
 *     `recordWindowMs` (additive, scope: temporal_echo) — +3000ms window.
 * - temporal_echo_upgrade_2 (Preserved State):
 *     On revert, fires condition `temporal_echo_revert` through CooldownHookSystem.
 *     The upgrade registers a hook that resets Phase Step cooldown on that condition.
 * - temporal_echo_passive (Foreshadow):
 *     Handled by ForesightPassiveSkillEffect — queries isRecording from the
 *     state system directly. No integration point here.
 *
 * Integration points:
 * - onLethalDamage(): called by HealthComponent before applying fatal damage.
 *   Returns true if the damage was intercepted (revert occurred).
 *
 * Phase sequence: stamp → recording window (6000ms base) → revert/expire → cooldown (30000ms).
 * Second activation during window triggers manual revert.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § temporal_echo
 * @see ADR-064 (attunement shared systems architecture)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { TemporalEchoStateSystem } from '../../../systems/TemporalEchoStateSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TemporalEchoDeps {
  /** Snapshot/rewind state system — manages position+HP stamps and reverts. */
  readonly temporalEcho: TemporalEchoStateSystem;
  /** Upgrade bonus resolver for recordWindowMs. */
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  /**
   * Cooldown hook system — notifies on revert so Preserved State
   * (temporal_echo_upgrade_2) can refund Phase Step cooldown.
   */
  readonly cooldownHooks: {
    notify(trigger: { type: 'onCondition'; conditionId: string }): void;
  };
  /** Provides the player's current position and HP for stamping. */
  readonly playerState: () => { x: number; y: number; hp: number };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TemporalEchoConfig {
  /** Base recording window in ms (6000ms). */
  readonly baseWindowMs: number;
  /** Cooldown after revert or expiry in ms (30000ms). */
  readonly cooldownMs: number;
}

const DEFAULT_CONFIG: TemporalEchoConfig = {
  baseWindowMs: 6000,
  cooldownMs: 30000,  // "One use every 30 seconds" per spec
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TemporalEchoSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'temporal_echo';

  readonly deps: TemporalEchoDeps;
  readonly config: TemporalEchoConfig;

  /** Resolved window duration set on each activation (base + upgrade bonus). */
  private _resolvedWindowMs = 0;

  /** Whether the most recent deactivation was caused by a revert (manual or lethal). */
  private _reverted = false;

  constructor(deps: TemporalEchoDeps, config: Partial<TemporalEchoConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get resolvedWindowMs(): number { return this._resolvedWindowMs; }
  get reverted(): boolean { return this._reverted; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  /**
   * Activate recording, or — if already recording — trigger manual revert.
   *
   * First call: stamps position+HP, begins recording window.
   * Second call during recording: reverts to stamp, starts cooldown.
   */
  activate(): boolean {
    if (this._isActive) {
      // Manual revert — second press during recording window.
      this.deps.temporalEcho.revert();
      this._reverted = true;
      this.deactivate();
      return true;
    }

    if (!this.canActivate()) return false;

    // Resolve upgrade: window extension from temporal_echo_upgrade_1.
    const windowBonus = this.deps.resolver.getAdditiveEffect('temporal_echo', 'recordWindowMs');
    this._resolvedWindowMs = this.config.baseWindowMs + windowBonus;

    // Stamp current position and HP.
    const state = this.deps.playerState();
    this.deps.temporalEcho.beginRecording(state.x, state.y, state.hp, this._resolvedWindowMs);

    this._isActive = true;
    this._reverted = false;
    return true;
  }

  /**
   * Lethal damage intercept — called by the health system before applying
   * fatal damage. If recording, reverts to stamp instead of dying.
   *
   * @returns true if damage was intercepted (revert occurred); false if not recording.
   */
  onLethalDamage(): boolean {
    if (!this._isActive || !this.deps.temporalEcho.isRecording) return false;
    this.deps.temporalEcho.revert();
    this._reverted = true;
    this.deactivate();
    return true;
  }

  protected override updateWhileActive(delta: number): void {
    // Tick the state system's countdown.
    this.deps.temporalEcho.update(delta);

    // Detect window expiry — state system auto-clears when timer reaches 0.
    if (!this.deps.temporalEcho.isRecording) {
      this._reverted = false;
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    // Fire Preserved State condition on revert (manual or lethal intercept).
    if (this._reverted) {
      this.deps.cooldownHooks.notify({
        type: 'onCondition',
        conditionId: 'temporal_echo_revert',
      });
    }

    // Start long cooldown.
    this.cooldownRemaining = this.config.cooldownMs;
  }

  override destroy(): void {
    if (this._isActive) {
      this.deps.temporalEcho.expire();
    }
    super.destroy();
  }
}
